// Global Instructions Rule Applied!

import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Checkbox, Modal, Select, Space, Typography } from 'antd'
import type { IndustryGridModel } from '../../../state_manager/data_models/industry_grid_model.ts'

const { Text } = Typography

interface ReplaceIndustryModalProps {
  open: boolean
  industries: IndustryGridModel[]
  initialSourceIds?: string[]
  confirmLoading?: boolean
  onConfirm: (sourceIds: string[], targetId: string, deleteSources: boolean) => void
  onCancel: () => void
}

// Replace (or merge) one-or-more industries into a single target: every account
// categorised with a source industry is reassigned to the target. This is also
// the forced flow when deleting an in-use industry.
export function ReplaceIndustryModal({
  open,
  industries,
  initialSourceIds,
  confirmLoading,
  onConfirm,
  onCancel
}: ReplaceIndustryModalProps) {
  const [sourceIds, setSourceIds] = useState<string[]>([])
  const [targetId, setTargetId] = useState<string | undefined>(undefined)
  const [deleteSources, setDeleteSources] = useState(true)

  // Reset the form whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setSourceIds(initialSourceIds ?? [])
      setTargetId(undefined)
      setDeleteSources(true)
    }
  }, [open, initialSourceIds])

  const sourceOptions = useMemo(
    () => industries.map((i) => ({ value: i.industries__id, label: i.industries__name })),
    [industries]
  )

  // The target cannot be one of the chosen sources.
  const targetOptions = useMemo(
    () =>
      industries
        .filter((i) => !sourceIds.includes(i.industries__id))
        .map((i) => ({ value: i.industries__id, label: i.industries__name })),
    [industries, sourceIds]
  )

  const affectedAccounts = useMemo(
    () =>
      industries
        .filter((i) => sourceIds.includes(i.industries__id))
        .reduce((sum, i) => sum + (i.industries__usage_count || 0), 0),
    [industries, sourceIds]
  )

  const targetName = industries.find((i) => i.industries__id === targetId)?.industries__name

  const canConfirm = sourceIds.length > 0 && !!targetId && !sourceIds.includes(targetId)

  return (
    <Modal
      title='Replace industry'
      open={open}
      okText='Reassign'
      okButtonProps={{ disabled: !canConfirm }}
      confirmLoading={confirmLoading}
      onOk={() => {
        if (canConfirm && targetId) onConfirm(sourceIds, targetId, deleteSources)
      }}
      onCancel={onCancel}
      destroyOnClose
    >
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <div>
          <Text strong>Industries to replace</Text>
          <Select
            mode='multiple'
            allowClear
            placeholder='Select one or more industries'
            value={sourceIds}
            options={sourceOptions}
            onChange={(vals) => {
              setSourceIds(vals)
              if (targetId && vals.includes(targetId)) setTargetId(undefined)
            }}
            optionFilterProp='label'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <div>
          <Text strong>Replace with</Text>
          <Select
            allowClear
            showSearch
            placeholder='Select the target industry'
            value={targetId}
            options={targetOptions}
            onChange={(val) => setTargetId(val)}
            optionFilterProp='label'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <Checkbox checked={deleteSources} onChange={(e) => setDeleteSources(e.target.checked)}>
          Delete the replaced {sourceIds.length === 1 ? 'industry' : 'industries'} after reassigning
        </Checkbox>

        {canConfirm && (
          <Alert
            type='info'
            showIcon
            message={
              <span>
                <Text strong>{affectedAccounts}</Text> account{affectedAccounts === 1 ? '' : 's'} will be
                recategorised to <Text strong>{targetName}</Text>
                {deleteSources ? '; the replaced industries will be deleted.' : '.'}
              </span>
            }
          />
        )}
      </Space>
    </Modal>
  )
}

export default ReplaceIndustryModal
